import React, { useEffect } from 'react';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faArrowRight, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import OrderService from '../../../../../services/orders/order';

// props
import { IOrderResourceProps, IOrderResourceShortProps, IOrderResourcerShorterProps } from '../../../../../props/orders/order';

// components
import { ActionButton, CommandBar, DatePicker, Dropdown, IButtonProps, ICommandBarItemProps, PrimaryButton, SpinnerSize, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import LoadingComponent from '../../../../feedbacks/loading';
import Label from '../../../../typography/label';
import Tag, { TTagVariant } from '../../../../uiframeworks/tag';
import GeneralService from '../../../../../services/general';
import ErrorService from '../../../../../services/general/error';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICompanyResourceShort } from '../../../../../props/users/company';
import { IFreightCategoryResourceShortProps } from '../../../../../props/orders/freightCategory';
import { IDeliveryTypeResourceShortProps } from '../../../../../props/orders/deliveryType';
import { SelectedFilterProps } from '../filters';
import { Bar, BarChart, CartesianGrid, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartColors from '../../../../../manifests/chartColors';

type OrdersDashboardTotalCustomersChartProps = {
    orders: IOrderResourceShortProps[];
    groupBy: string;
}

type ChartData = {
    name: string;
    key: string;
    legend: string;
    fill: string;
    value: number;
};

const OrdersDashboardTotalCustomersChart: React.FC<OrdersDashboardTotalCustomersChartProps> = (props: OrdersDashboardTotalCustomersChartProps) => {
    const { banner } = useStore();

    const groupData = () => {
        const data: ChartData[] = [];

        let grouped: { key?: ICompanyResourceShort, value: number }[] = [];

        // group all orders based on customer/company
        props.orders.forEach((order) => {
            const idx = grouped.findIndex((m) => m.key?.id === (order.company?.id));
            if (idx < 0) {
                grouped.push({
                    key: order.company,
                    value: 1
                });
            } else if (grouped[idx]) {
                grouped[idx].value = grouped[idx].value + 1;
            }
        });

        // sort group based on month
        grouped = grouped.sort((a,b) => (a.value > b.value) ? 1 : ((b.value > a.value) ? -1 : 0)).reverse();
        grouped.forEach((group, idx) => {
            if (idx < 7) {
                const name = group.key?.name || "Unknown";

                data.push({
                    key: group.key?.id || "__sys_unknown",
                    name,
                    legend: name,
                    fill: ChartColors[idx],
                    value: group.value
                });
            } else if (idx === 7) {
                data.push({
                    key: "Others",
                    name: "Others",
                    legend: "Others",
                    fill: ChartColors[idx],
                    value: group.value
                });
            } else {
                data[data.length - 1].value += group.value;
            }
        });

        return data;
    }

    const renderLegend = (props: any) => {
        const { payload } = props;

        return <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign='center' wrap>
            {payload.map((entry: any, index: number) => {
                return <Stack key={entry.value || index} horizontal tokens={{ childrenGap: 5 }}>
                    <Stack.Item className={styles.indicator} styles={{ root: { backgroundColor: entry.color } }}> </Stack.Item>
                    <Text size={'small'}>{entry.payload.legend}</Text>
                </Stack>
            })}
        </Stack>;
    }

    return <Stack className={styles.container} styles={{ root: { width: '100%', height: 300 } }}>
        <ResponsiveContainer>
            <PieChart>
                <Pie dataKey="value"
                    data={groupData()}
                    label={entry => `${entry.name}\n(${GeneralService.getNumberWithSeparator(entry.value)})`}
                    paddingAngle={5}
                    innerRadius={60}
                    outerRadius={80} />
                <Legend content={renderLegend} verticalAlign="bottom" align="center" />
            </PieChart>
        </ResponsiveContainer>
    </Stack>;
};

export default OrdersDashboardTotalCustomersChart;
